export interface ExtractedColor {
    hex: string;
    rgb: [number, number, number];
    hsl: [number, number, number];
    population: number;
}

export interface UIColor {
    hex: string;
    source: string;
}

export interface CSSColor {
    name: string;
    value: string;
}

export interface BrandLogo {
    src: string;
    source: string;
    alt?: string;
    width?: number;
    height?: number;
}

export interface FontFileData {
    dataUrl: string;
    format: 'ttf' | 'otf';
}

export interface BrandFonts {
    heading: string | null;
    body: string | null;
    headingRaw: string | null;
    bodyRaw: string | null;
    loadedFamilies: string[];
    cssVars: Record<string, string>;
    fontFiles?: Record<string, FontFileData>;
}

export interface BrandKitData {
    url: string;
    screenshot: {
        dataUrl: string;
        width: number;
        height: number;
    };
    colors: {
        fromScreenshot: {
            brand: ExtractedColor[];
            neutral: ExtractedColor[];
        };
        fromUI: UIColor[];
        fromCSS: CSSColor[];
    };
    fonts: BrandFonts;
    logos: BrandLogo[];
    meta: {
        title: string | null;
        description: string | null;
        themeColor: string | null;
    };
}

class BrandKitRegistry {
    private static instance: BrandKitRegistry;
    private registry: Map<string, BrandKitData> = new Map();
    private latestId: string | null = null;

    public static getInstance(): BrandKitRegistry {
        if (!BrandKitRegistry.instance) {
            BrandKitRegistry.instance = new BrandKitRegistry();
        }
        return BrandKitRegistry.instance;
    }

    private generateId(): string {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        for (let i = 0; i < 6; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }

        if (this.registry.has(result)) {
            return this.generateId();
        }

        return result;
    }

    public addExtraction(data: BrandKitData): string {
        const id = this.generateId();
        this.registry.set(id, data);
        this.latestId = id;
        return id;
    }

    public getExtraction(id: string): BrandKitData | null {
        return this.registry.get(id) ?? null;
    }

    public getLatestExtractionId(): string | null {
        return this.latestId;
    }

    public findFontFile(family: string): FontFileData | null {
        for (const [, data] of this.registry) {
            const fontFile = data.fonts.fontFiles?.[family];
            if (fontFile) return fontFile;
        }
        return null;
    }

    public clear(): void {
        this.registry.clear();
    }
}

export { BrandKitRegistry };
